<template>
	<div class="parent-quote" @click="$emit('open')">
		<div class="parent-header">
			<ReplyIcon class="parent-icon" />
			<i18n-t keypath="chat_message_quoted_header">
				<div
					class="parent-user"
					v-text="user ? user.fullname : $t('chat_message_unknownuser')"
				/>
			</i18n-t>
		</div>
		<div class="parent-content">
			<div class="parent-media" v-if="message && message.hasMedia">
				<img
					class="parent-img ph-no-capture"
					v-if="media.isPhoto"
					:src="media.mediaUrl"
				/>
				<FileIcon
					class="parent-file"
					v-if="media.isFile"
					:icon="media.fileIcon"
					:ext="media.extension"
				/>
				<div class="parent-video" v-if="media.isVideo">
					<img
						class="parent-video-thumb ph-no-capture"
						:src="media.thumbnailUrl"
					/>
					<div class="parent-video-icon">
						<MediaPlayArrowIcon />
					</div>
				</div>
				<div class="parent-audio" v-if="media.isAudio">
					<AudioMessageIcon />
				</div>
			</div>
			<div class="parent-media" v-if="hasPoll">
				<SquarePollHorizontalIcon class="parent-poll" type="solid" />
			</div>
			<div
				class="parent-link"
				v-if="showLinkCard && resource && resource.thumbnailurl"
			>
				<img class="parent-link-img" :src="resource.thumbnailurl" />
			</div>
			<div class="parent-text-container" :class="{ 'has-thumb': hasThumbnail }">
				<div
					class="parent-text"
					:class="{
						missing: !message,
						system: isSystemEvent,
						'line-clamp-1': titleText,
						'line-clamp-2': !titleText,
					}"
					v-text="parentText"
					data-private
				/>
				<div class="parent-text-title" v-if="titleText" v-text="titleText" />
			</div>
		</div>
	</div>
</template>

<script>
import { getMessage, getUser } from '@/store'
import { FileIcon } from '@/components/common'
import {
	ReplyIcon,
	MediaPlayArrowIcon,
	AudioMessageIcon,
	SquarePollHorizontalIcon,
} from '@/icons'
import { parseMessageParts, partType } from './utils'
import { buildSystemMessage } from '@/helperMethods'
import { mediaTypes } from '@/helpers/constants'

export const defaultMediaText = {
	[mediaTypes.PHOTO]: 'chat_message_mediaaction_photo',
	[mediaTypes.VIDEO]: 'chat_message_mediaaction_video',
	[mediaTypes.FILE]: 'chat_message_mediaaction_file',
	[mediaTypes.AUDIO]: 'chat_message_mediaaction_audio',
}

export default {
	name: 'ParentQuote',
	props: ['messageId'],
	components: {
		ReplyIcon,
		MediaPlayArrowIcon,
		AudioMessageIcon,
		FileIcon,
		SquarePollHorizontalIcon,
	},

	data() {
		return {
			message: null,
			user: null,
		}
	},

	emits: ['open'],

	mounted() {
		this.updateMessage()
		this.updateUser()

		this.$onEventBus({
			'message-change': (ids) => {
				if (ids?.includes(this.messageId)) {
					this.updateMessage()
				}
			},
			'user-update': (id) => {
				if (this.message?.user_id === id) {
					this.updateUser()
				}
			},
		})
	},

	computed: {
		messageParts() {
			if (!this.message) return []
			return parseMessageParts(this.message)
		},

		media() {
			return this.message?.media
		},

		resource() {
			return this.message?.resource
		},

		isSystemEvent() {
			return this.message?.systemevent
		},

		hasThumbnail() {
			if (!this.message) return false
			if (this.message.hasMedia && !this.message.media.isFile) return true
			return this.showLinkCard && this.resource?.thumbnailurl
		},

		parentText() {
			return (
				this.text || this.resource?.providername || this.defaultText || null
			)
		},

		text() {
			if (!this.message) return this.$t('chat_message_quoted_deleted')
			if (this.message.systemevent) {
				return buildSystemMessage(this.message)
			}
			if (this.hasPoll) return this.$t('chat_message_quoted_poll')
			return this.messageParts
				.map(({ type, userId, user, text }) => {
					switch (type) {
						case partType.MENTION:
							if (user) return `@${user.fullname}`
							if (userId === 'everyone')
								return this.$t('model_mentions_moniker_everyone')
							return `@${this.$t('model_user_noname', [userId])}`
						case partType.HYPERLINK:
						case partType.TEXT:
							return text
						default:
							return null
					}
				})
				.filter((part) => part)
				.join('')
		},

		defaultText() {
			if (!this.message?.hasMedia) return null
			if (this.message.mediaCount > 1) {
				return this.$t('chat_message_mediaaction_multiple', {
					Count: this.message.mediaCount,
				})
			}
			if (defaultMediaText[this.message.media.mediatype]) {
				return this.$t(defaultMediaText[this.message.media.mediatype])
			}
			return null
		},

		showLinkCard() {
			return (
				!this.media &&
				this.resource &&
				this.messageParts.find((part) => part.type === partType.LINK_CARD)
			)
		},

		titleText() {
			if (this.hasPoll) return this.message.poll.question
			if (this.showLinkCard) return this.message.resource.title
			if (this.message?.task)
				return this.$t('chat_message_quoted_task', [this.message.task.title])
			if (this.message?.mediaCount > 1 && this.text)
				return this.$t('chat_message_mediaaction_multiple_short', {
					Count: this.message.mediaCount,
				})
			if (this.media?.isFile && this.message.mediaCount === 1)
				return this.media.title
			return null
		},

		hasPoll() {
			return this.message?.poll_id
		},
	},

	methods: {
		updateMessage() {
			this.message = getMessage(this.messageId)
			this.updateUser()
		},

		updateUser() {
			this.user = getUser(this.message?.user_id)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$img-size: 34px;
$img-margin: 8px;

.parent-quote {
	cursor: pointer;
	margin: 10px 0 10px 14px;
	position: relative;
	z-index: 2;
	.parent-header {
		display: flex;
		align-items: center;
		font-size: 12px;
		color: var(--neutral-600);
		font-family: Avenir-Heavy;
		margin-left: 8px;
		margin-bottom: 4px;
		max-width: 100%;
		overflow: hidden;
		white-space: nowrap;
		.parent-icon {
			width: 15px;
			height: 13px;
			margin: 0 6px;
			:deep(path) {
				fill: var(--teal-300);
			}
		}
		.parent-user {
			margin-left: 4px;
			color: var(--neutral-800);
			max-width: 100%;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
	}
	.parent-content {
		padding: 8px;
		background: var(--neutral-100);
		border-radius: 6px;
		width: max-content;
		min-width: 280px;
		max-width: 100%;
		display: flex;
		align-items: center;
		.parent-text-container {
			color: var(--neutral-600);
			font-size: 14px;
			width: 100%;
			overflow: hidden;
			&.has-thumb {
				max-width: calc(100% - #{$img-size} - #{$img-margin});
			}
			.parent-text {
				font-family: Avenir-Roman;
				&.system,
				&.missing {
					width: 100%;
					font-style: italic;
				}
				&.missing {
					text-align: center;
				}
				&.has-title {
					max-height: 24px;
				}
			}
			.parent-text-title {
				font-family: Avenir-Heavy;
			}
			.parent-text-title > div {
				width: 100%;
				max-width: 100%;
				overflow: hidden;
				white-space: nowrap;
				text-overflow: ellipsis;
			}
		}

		.parent-media {
			margin-right: $img-margin;
			height: $img-size;
			.parent-img {
				width: $img-size;
				height: $img-size;
				object-fit: cover;
				border-radius: 6px;
			}
			.parent-file {
				font-size: 32px;
				object-fit: contain;
			}
			.parent-video {
				position: relative;
				z-index: 0;
				border-radius: 6px;
				overflow: hidden;
				width: $img-size;
				height: $img-size;
				.parent-video-thumb {
					width: $img-size;
					height: $img-size;
					object-fit: cover;
				}
				.parent-video-icon {
					height: $img-size;
					position: absolute;
					background: rgba(0, 0, 0, 0.4);
					z-index: 1;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					display: flex;
					align-items: center;
					justify-content: center;
					:deep(svg) {
						width: 15px;
						height: 18px;
					}
				}
			}
			.parent-audio {
				.pronto-icon {
					font-size: 35px;
					color: var(--teal-300);
				}
			}
			.parent-poll {
				font-size: 35px;
				color: var(--teal-300);
			}
		}
		.parent-link {
			margin-right: $img-margin;
			height: $img-size;
			.parent-link-img {
				width: $img-size;
				height: $img-size;
				object-fit: contain;
			}
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.parent-quote {
		.parent-content {
			background: var(--neutral-300);
		}
	}
}
</style>
